import {
  HANDOVER_LIMITS,
  type HandoverCatalogsResponse,
  type HandoverPhoto,
  type InspectionItem,
  handoverCatalogsResponseSchema,
  suggestContractor,
} from '@sivan/shared';
import { useEffect, useEffectEvent, useId, useState } from 'react';
import { apiGet } from '../api/client';
import { useFieldMode } from '../master-data/use-narrow-screen';
import { Value } from '../master-data/Value';
import { CatalogPicker } from './CatalogPicker';
import { PhotoField } from './PhotoField';
import { PhotoViewer } from './PhotoViewer';
import { type FindingErrors, type FindingFields, validateFinding } from './handover-draft';

/** The selected contractor's contact, when the user may see it. */
export function ContractorContact({
  catalogs,
  contractorId,
}: {
  readonly catalogs: HandoverCatalogsResponse;
  readonly contractorId: string | null;
}) {
  const contact = catalogs.contractors.find((c) => c.id === contractorId)?.contact;
  if (contact == null) return null;
  const { contactName, contactPhone, contactEmail } = contact;
  if (contactName === null && contactPhone === null && contactEmail === null) return null;
  return (
    <p className="contact-card">
      {contactName !== null && <span className="contact-card__name">{contactName}</span>}
      {contactPhone !== null && (
        <a className="contact-card__phone" href={`tel:${contactPhone.replace(/[^0-9+]/g, '')}`}>
          <Value text={contactPhone} />
        </a>
      )}
      {contactEmail !== null && (
        <a className="contact-card__email" href={`mailto:${contactEmail}`}>
          <Value text={contactEmail} />
        </a>
      )}
    </p>
  );
}

/**
 * Add or edit one finding: area, component and contractor from the project catalogs, a free-text
 * description, the inspection date and photos. A finding on an inspection item takes its area,
 * work category and name from the item. The contractor: the work category's default is suggested
 * (and can be changed); with several contractors and no default, one must be chosen.
 */
export function FindingForm({
  item,
  initial,
  catalogs: loadedCatalogs,
  catalogsPath,
  uploadPath,
  photoUrl,
  submitLabel,
  onSubmit,
  initialFiles,
  onCancel,
  onUploaded,
  onFieldsChange,
  restored = false,
}: {
  /** The inspection item the finding is on, if any. */
  readonly item?: InspectionItem | undefined;
  readonly initial: FindingFields;
  readonly catalogs: HandoverCatalogsResponse;
  /** Re-read when the dialog opens, so catalog changes show without reloading the page. */
  readonly catalogsPath: string;
  readonly uploadPath: string;
  readonly photoUrl: (photo: HandoverPhoto) => string;
  readonly submitLabel: string;
  readonly onSubmit: (fields: FindingFields) => void;
  /** Photos taken before the form opened ("צילום ליקוי"): uploaded at once. */
  readonly initialFiles?: readonly File[] | undefined;
  readonly onCancel: () => void;
  /** Each photo uploaded in this dialog, so an abandoned upload can be cleaned up. */
  readonly onUploaded: (photo: HandoverPhoto) => void;
  /**
   * The fields as they are now, and whether they hold something not yet in the draft (changed
   * since the dialog opened, or restored after a sign-in or a refresh).
   */
  readonly onFieldsChange?: (fields: FindingFields, changed: boolean) => void;
  /** The fields were restored from this tab (shown to the user). */
  readonly restored?: boolean;
}) {
  const [edited, setFields] = useState<FindingFields>(initial);
  const [errors, setErrors] = useState<FindingErrors>({});
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState<number | null>(null);
  const [catalogs, setCatalogs] = useState(loadedCatalogs);
  const [cleared, setCleared] = useState<string | null>(null);
  // Until the user picks a contractor, the category's default is filled in for them.
  const [picked, setPicked] = useState(initial.contractorId !== null);
  const id = useId();
  // On a phone the photo comes first (Design Gate 1.2), in the document order too, so the
  // keyboard and screen readers meet the fields in the order they are shown.
  const photosFirst = useFieldMode();
  const suggestion = suggestContractor(
    edited.workCategoryId,
    catalogs.workCategories,
    catalogs.contractors,
  );
  const suggested = suggestion.kind === 'default' ? suggestion.contractorId : null;
  const preset = picked ? undefined : catalogs.contractors.find((c) => c.id === suggested);
  // The fields as shown and submitted: the edits, with the suggested contractor until one is picked.
  const fields: FindingFields =
    preset === undefined ? edited : { ...edited, contractorId: preset.id, contractor: preset.name };
  // A new finding, or a new category, with several contractors and no default needs a choice.
  const mustChoose =
    suggestion.kind === 'choose' &&
    fields.status === 'open' &&
    (initial.contractorId === null || fields.workCategoryId !== initial.workCategoryId);
  useEffect(() => {
    let current = true;
    apiGet(catalogsPath, handoverCatalogsResponseSchema).then(
      (fresh) => {
        if (current) setCatalogs(fresh);
      },
      () => undefined, // keep the catalogs the editor loaded
    );
    return () => {
      current = false;
    };
  }, [catalogsPath]);
  // Contractors of the chosen work category (and the current choice, so it stays visible).
  // Compared with the fields as first shown (with the suggested contractor already filled in).
  const snapshot = JSON.stringify(fields);
  const [baseline] = useState(snapshot);
  const report = useEffectEvent((current: FindingFields) => {
    onFieldsChange?.(current, restored || JSON.stringify(current) !== baseline);
  });
  useEffect(() => {
    report(JSON.parse(snapshot) as FindingFields);
  }, [snapshot]);
  const contractors =
    fields.workCategoryId === null
      ? catalogs.contractors
      : catalogs.contractors.filter(
          (c) =>
            c.categoryIds.includes(fields.workCategoryId ?? '') || c.id === fields.contractorId,
        );
  const set = (change: Partial<FindingFields>, key: keyof FindingErrors) => {
    setFields((f) => ({ ...f, ...change }));
    if (errors[key] !== undefined) setErrors((e) => ({ ...e, [key]: undefined }));
  };
  const photoBlock = (
    <>
      <PhotoField
        photos={fields.photos}
        uploadPath={uploadPath}
        urlOf={photoUrl}
        error={errors.photos}
        onBusy={setBusy}
        onOpen={setViewing}
        initialFiles={initialFiles}
        onAdd={(photo) => {
          onUploaded(photo);
          setFields((f) => ({ ...f, photos: [...f.photos, photo] }));
        }}
        onRemove={(photo) => {
          setFields((f) => ({ ...f, photos: f.photos.filter((p) => p.id !== photo.id) }));
        }}
      />
      <PhotoViewer
        photos={fields.photos}
        index={viewing}
        urlOf={photoUrl}
        onIndex={setViewing}
        onClose={() => {
          setViewing(null);
        }}
      />
    </>
  );
  return (
    <form
      className="finding-form"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        const found: FindingErrors = { ...validateFinding(fields) };
        if (mustChoose && fields.contractorId === null)
          found.contractor = 'יש לבחור קבלן: בתחום זה יש כמה קבלנים ואין קבלן ברירת מחדל.';
        setErrors(found);
        if (Object.keys(found).length === 0) onSubmit(fields);
      }}
    >
      {restored && (
        <p className="notice notice--info" role="status">
          שוחזר מה שנרשם בליקוי זה לפני שהחיבור נקטע או שהדף נטען מחדש. הוא עדיין לא נוסף לטיוטה.
        </p>
      )}
      {item !== undefined ? (
        <p className="finding-form__context" aria-label="פריט הבדיקה">
          <span>{item.area}</span>
          <span aria-hidden="true">·</span>
          <span>{item.workCategory}</span>
          <span aria-hidden="true">·</span>
          <strong>{item.name}</strong>
          {item.component !== null && item.component !== item.name && (
            <>
              <span aria-hidden="true">·</span>
              <span>רכיב: {item.component}</span>
            </>
          )}
        </p>
      ) : (
        <div className="finding-form__pair">
          <CatalogPicker
            label="חדר"
            entries={catalogs.areas}
            value={{ id: fields.areaId, text: fields.area }}
            error={errors.area}
            onChange={(entry) => {
              set({ areaId: entry?.id ?? null, area: entry?.name ?? '' }, 'area');
            }}
          />
          <CatalogPicker
            label="רכיב לבדיקה"
            entries={catalogs.components}
            value={{ id: fields.componentId, text: fields.component }}
            error={errors.component}
            onChange={(entry) => {
              set({ componentId: entry?.id ?? null, component: entry?.name ?? '' }, 'component');
            }}
          />
        </div>
      )}
      {photosFirst && photoBlock}
      <label className="field">
        <span>תיאור הליקוי</span>
        <textarea
          value={fields.description}
          rows={3}
          maxLength={HANDOVER_LIMITS.description + 20}
          aria-invalid={errors.description !== undefined}
          aria-describedby={
            errors.description === undefined ? undefined : `${id}-description-error`
          }
          onChange={(e) => {
            set({ description: e.target.value }, 'description');
          }}
        />
        {errors.description !== undefined && (
          <p className="field__error" id={`${id}-description-error`}>
            {errors.description}
          </p>
        )}
      </label>
      <div className="finding-form__pair">
        <div className="field">
          {item === undefined && (
            <CatalogPicker
              label="תחום עבודה (רשות)"
              entries={catalogs.workCategories}
              value={{ id: fields.workCategoryId, text: fields.workCategory }}
              clearable
              onChange={(entry) => {
                const categoryId = entry?.id ?? null;
                const keeps =
                  categoryId === null ||
                  edited.contractorId === null ||
                  (catalogs.contractors
                    .find((c) => c.id === edited.contractorId)
                    ?.categoryIds.includes(categoryId) ??
                    false);
                // A contractor outside the new category is cleared, and the user is told so.
                setCleared(keeps ? null : edited.contractor);
                set(
                  {
                    workCategoryId: categoryId,
                    workCategory: entry?.name ?? '',
                    ...(keeps ? {} : { contractorId: null, contractor: '' }),
                  },
                  'contractor',
                );
              }}
            />
          )}
          <CatalogPicker
            label={mustChoose ? 'קבלן / ספק' : 'קבלן / ספק (רשות)'}
            entries={contractors}
            value={{ id: fields.contractorId, text: fields.contractor }}
            clearable={!mustChoose}
            error={errors.contractor}
            hint={
              cleared !== null
                ? `הקבלן "${cleared}" אינו משויך לתחום שנבחר ולכן הוסר. בחרו קבלן מהתחום.`
                : suggested !== null && fields.contractorId === suggested
                  ? 'הוצע אוטומטית: קבלן ברירת המחדל של התחום. ניתן לשנות.'
                  : mustChoose
                    ? 'בתחום זה יש כמה קבלנים ואין קבלן ברירת מחדל: יש לבחור אחד.'
                    : fields.workCategoryId !== null
                      ? 'מוצגים קבלנים מהתחום שנבחר.'
                      : undefined
            }
            onChange={(entry) => {
              setCleared(null);
              setPicked(true);
              set({ contractorId: entry?.id ?? null, contractor: entry?.name ?? '' }, 'contractor');
            }}
          />
          <ContractorContact catalogs={catalogs} contractorId={fields.contractorId} />
        </div>
        <label className="field">
          <span>תאריך בדיקה</span>
          <input
            type="date"
            value={fields.inspectedOn}
            aria-invalid={errors.inspectedOn !== undefined}
            aria-describedby={errors.inspectedOn === undefined ? undefined : `${id}-date-error`}
            onChange={(e) => {
              set({ inspectedOn: e.target.value }, 'inspectedOn');
            }}
          />
          {errors.inspectedOn !== undefined && (
            <p className="field__error" id={`${id}-date-error`}>
              {errors.inspectedOn}
            </p>
          )}
        </label>
      </div>
      {!photosFirst && photoBlock}
      <div className="form-actions">
        <button type="submit" className="button button--primary" disabled={busy}>
          {busy ? 'ממתין לסיום ההעלאה…' : submitLabel}
        </button>
        <button type="button" className="button" onClick={onCancel}>
          ביטול
        </button>
      </div>
    </form>
  );
}
